<template>
  <div v-if="visible" class="strategy-exception" @click.self="close">
    <section
      v-if="phase === 'warning'"
      class="strategy-exception__warning"
      role="dialog"
      aria-modal="true"
      aria-labelledby="strategy-exception-title"
    >
      <header class="strategy-exception__warning-header">
        <div>
          <span class="strategy-exception__warning-icon">
            <i class="el-icon-warning-outline" />
          </span>
          <div>
            <h2 id="strategy-exception-title">策略异常</h2>
            <small>机器人已自动暂停</small>
          </div>
        </div>
        <button type="button" aria-label="关闭" @click="close">
          <i class="el-icon-close" />
        </button>
      </header>
      <div class="strategy-exception__warning-content">
        <strong>已达到最大持仓</strong>
        <p>本策略因达到最大持仓而暂停运行，请修改最大持仓后重启策略。</p>
      </div>
      <footer>
        <button type="button" @click="close">知道了</button>
        <button type="button" class="modify" @click="$emit('modify')">去修改</button>
      </footer>
    </section>

    <section
      v-else
      class="strategy-exception__modify"
      role="dialog"
      aria-modal="true"
      aria-labelledby="strategy-maximum-title"
    >
      <header>
        <div>
          <h2 id="strategy-maximum-title">修改最大持仓</h2>
          <p>{{ robot.exchange }} · {{ robot.symbol }}</p>
        </div>
        <button type="button" :disabled="submitting" aria-label="关闭" @click="close">
          <i class="el-icon-close" />
        </button>
      </header>

      <div class="strategy-exception__field">
        <div>
          <label for="maximum-position">新的最大持仓</label>
          <label class="strategy-exception__infinite">
            <input v-model="infinite" type="checkbox" />
            无限
          </label>
        </div>
        <div v-if="!infinite" class="strategy-exception__input">
          <input
            id="maximum-position"
            v-model.trim="maxPosition"
            inputmode="decimal"
            :placeholder="`> ${minimumPosition}`"
            @keyup.enter="submit"
          />
          <span>USDT</span>
        </div>
        <div v-else class="strategy-exception__infinite-tip">无限大</div>
        <small>新的最大持仓不能小于 {{ minimumPosition }} USDT</small>
      </div>

      <div class="strategy-exception__notice">
        UPlus 不会对您的任何交易行为承担担保、赔偿等责任。点击确认代表同意本条款。
      </div>

      <footer>
        <button type="button" :disabled="submitting" @click="close">取消</button>
        <button type="button" class="restart" :disabled="submitting" @click="submit">
          <i v-if="submitting" class="el-icon-loading" />
          {{ submitting ? '正在重启' : '确认修改并重启' }}
        </button>
      </footer>
    </section>
  </div>
</template>

<script>
import { minimumRestartPosition } from '@/api/robot-actions'

function finiteNumber(value) {
  if (value === '' || value === null || value === undefined) return null
  const number = Number(value)
  return Number.isFinite(number) ? number : null
}

export default {
  name: 'RobotStrategyExceptionModal',
  props: {
    visible: { type: Boolean, default: false },
    phase: { type: String, default: 'warning' },
    robot: { type: Object, default: () => ({ raw: {} }) },
    submitting: { type: Boolean, default: false }
  },
  data() {
    return {
      infinite: false,
      maxPosition: ''
    }
  },
  computed: {
    minimumPosition() {
      return minimumRestartPosition(this.robot)
    }
  },
  watch: {
    visible(value) {
      if (value) this.reset()
    },
    phase(value, oldValue) {
      if (value === 'modify' && oldValue !== 'modify') this.reset()
    },
    robot() {
      if (this.visible) this.reset()
    }
  },
  methods: {
    reset() {
      this.infinite = false
      this.maxPosition = ''
    },
    close() {
      if (!this.submitting) this.$emit('update:visible', false)
    },
    submit() {
      if (this.submitting) return
      if (!this.infinite) {
        const maximum = finiteNumber(this.maxPosition)
        if (maximum === null) {
          this.$message.warning(this.$t('请输入最大持仓设置'))
          return
        }
        if (maximum < this.minimumPosition) {
          this.$message.warning(this.$t(`最大持仓不能小于 ${this.minimumPosition} USDT`))
          return
        }
      }
      this.$emit('submit', {
        robot: this.robot,
        form: {
          infinite: this.infinite,
          maxPosition: this.infinite ? '0' : this.maxPosition
        }
      })
    }
  }
}
</script>

<style scoped>
.strategy-exception {
  position: fixed;
  z-index: 170;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(20, 17, 38, .46);
  animation: up-fade-in .18s ease;
}

.strategy-exception__warning {
  width: 470px;
  max-width: 92vw;
  padding: 26px;
  color: #1b1f2a;
  border-radius: 16px;
  background: #fff;
  box-shadow: 0 24px 64px rgba(20, 16, 45, .28);
  animation: strategy-exception-pop .2s ease;
}

.strategy-exception__warning-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
}

.strategy-exception__warning-header > div {
  display: flex;
  align-items: center;
  gap: 12px;
}

.strategy-exception__warning-icon {
  display: grid;
  width: 42px;
  height: 42px;
  flex: 0 0 auto;
  place-items: center;
  color: #d97706;
  border-radius: 11px;
  background: #fff3df;
  font-size: 20px;
}

.strategy-exception__warning h2,
.strategy-exception__warning small,
.strategy-exception__warning p {
  margin: 0;
}

.strategy-exception__warning h2 {
  font-size: 18px;
  font-weight: 800;
}

.strategy-exception__warning small {
  display: block;
  margin-top: 4px;
  color: #8a90a0;
  font-size: 12px;
}

.strategy-exception__warning-header > button {
  display: grid;
  width: 30px;
  height: 30px;
  flex: 0 0 auto;
  padding: 0;
  place-items: center;
  color: #8a90a0;
  border: 0;
  border-radius: 8px;
  background: transparent;
  cursor: pointer;
}

.strategy-exception__warning-header > button:hover {
  color: #5a6072;
  background: #f5f6f9;
}

.strategy-exception__warning-content {
  margin-top: 20px;
  padding: 15px;
  color: #7a5b12;
  border: 1px solid #f4dfb4;
  border-radius: 10px;
  background: #fff8e8;
}

.strategy-exception__warning-content strong {
  display: block;
  font-size: 14px;
}

.strategy-exception__warning-content p {
  margin-top: 6px;
  color: #7b6740;
  font-size: 13px;
  line-height: 1.65;
}

.strategy-exception__warning footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 22px;
  padding-top: 16px;
  border-top: 1px solid #eef0f5;
}

.strategy-exception__warning footer button {
  min-width: 90px;
  height: 38px;
  padding: 0 18px;
  color: #3a3f4e;
  border: 1px solid #dcdfe8;
  border-radius: 8px;
  background: #fff;
  font-size: 13.5px;
  font-weight: 700;
  cursor: pointer;
  transition: border-color .16s ease, background-color .16s ease, color .16s ease;
}

.strategy-exception__warning footer button:hover {
  border-color: #bfc5d2;
  background: #f7f8fb;
}

.strategy-exception__warning footer .modify {
  color: #fff;
  border-color: #1f6bff;
  background: #1f6bff;
}

.strategy-exception__warning footer .modify:hover {
  border-color: #1558db;
  background: #1558db;
}

@keyframes strategy-exception-pop {
  from { opacity: 0; transform: translateY(10px) scale(.985); }
  to { opacity: 1; transform: translateY(0) scale(1); }
}

.strategy-exception__modify {
  width: 470px;
  max-width: 92vw;
  padding: 26px;
  color: #1b1f2a;
  border-radius: 16px;
  background: #fff;
  box-shadow: 0 24px 70px rgba(20, 16, 45, .3);
}

.strategy-exception__modify header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 20px;
}

.strategy-exception__modify h2,
.strategy-exception__modify header p {
  margin: 0;
}

.strategy-exception__modify h2 {
  font-size: 19px;
}

.strategy-exception__modify header p {
  margin-top: 5px;
  color: #8a90a0;
  font-size: 12px;
}

.strategy-exception__modify header > button {
  width: 30px;
  height: 30px;
  color: #8a90a0;
  border: 0;
  border-radius: 8px;
  background: transparent;
  cursor: pointer;
}

.strategy-exception__modify header > button:hover {
  background: #f5f6f9;
}

.strategy-exception__field > div:first-child {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.strategy-exception__field label {
  color: #3a3f4e;
  font-size: 13px;
  font-weight: 700;
}

.strategy-exception__infinite {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-weight: 500 !important;
  cursor: pointer;
}

.strategy-exception__input {
  display: flex;
  align-items: center;
  margin-top: 9px;
  border: 1px solid #dfe3eb;
  border-radius: 9px;
}

.strategy-exception__input:focus-within {
  border-color: #1f6bff;
  box-shadow: 0 0 0 3px rgba(31, 107, 255, .1);
}

.strategy-exception__input input {
  width: 100%;
  height: 40px;
  padding: 0 12px;
  border: 0;
  outline: none;
  background: transparent;
}

.strategy-exception__input span {
  padding-right: 12px;
  color: #8a90a0;
  font-size: 12px;
}

.strategy-exception__field small {
  display: block;
  margin-top: 7px;
  color: #8a90a0;
  font-size: 11px;
}

.strategy-exception__infinite-tip {
  margin-top: 9px;
  padding: 12px;
  color: #0b7a47;
  border-radius: 9px;
  background: #f0faf4;
  font-size: 12px;
}

.strategy-exception__notice {
  margin-top: 16px;
  padding: 11px 12px;
  color: #7a5b12;
  border-radius: 9px;
  background: #fff8e8;
  font-size: 11px;
  line-height: 1.6;
}

.strategy-exception__modify footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
}

.strategy-exception__modify footer button {
  min-width: 98px;
  height: 38px;
  padding: 0 18px;
  color: #3a3f4e;
  border: 1px solid #dcdfe8;
  border-radius: 8px;
  background: #fff;
  font-weight: 700;
  cursor: pointer;
}

.strategy-exception__modify footer .restart {
  min-width: 142px;
  color: #fff;
  border-color: #1f6bff;
  background: #1f6bff;
}

.strategy-exception__modify footer .restart:hover {
  border-color: #1558db;
  background: #1558db;
}

.strategy-exception__modify footer button:disabled {
  cursor: not-allowed;
  opacity: .65;
}
</style>
